import * as Cesium from "cesium";
import { cloneEntity } from "../Utility";

/**
 * offsetSelectedPolylineOnce
 * --------------------------
 * One-shot click to create a parallel polyline offset from a clicked polyline.
 * If `defaultDataSource` is provided, only entities that belong to that DataSource
 * will be accepted when the user clicks.
 */
export function offsetSelectedPolylineOnce({ viewer, distanceMeters, direction, pushUndo, defaultDataSource }) {
  if (!viewer || !viewer.scene || !viewer.scene.canvas) {
    console.warn("[offsetSelectedPolylineOnce] Invalid viewer.");
    return null;
  }
  if (!(typeof distanceMeters === "number" && isFinite(distanceMeters) && distanceMeters > 0)) {
    console.warn("[offsetSelectedPolylineOnce] 'distanceMeters' must be a positive number.");
    return null;
  }
  const side = String(direction || "").toLowerCase();
  if (side !== "left" && side !== "right") {
    console.warn("[offsetSelectedPolylineOnce] 'direction' must be 'left' or 'right'.");
    return null;
  }
  if (typeof pushUndo !== "function") {
    console.warn("[offsetSelectedPolylineOnce] 'pushUndo' is required for undo/redo integration.");
    return null;
  }

  let cancelFn;

  const promise = new Promise((resolve, reject) => {
    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);

    const selectedEntities = [];
    const styleByEntityId = new Map(); // id -> { width, material, clampToGround, arcType, zIndex }
    let keyListener;

    const cleanup = () => {
      try {
        handler.destroy();
      } catch (_) {}

      if (keyListener) {
        try {
          window.removeEventListener("keydown", keyListener, true);
        } catch (_) {}
        keyListener = null;
      }

      for (const e of selectedEntities) {
        try {
          const saved = styleByEntityId.get(e.id);
          if (saved && e?.polyline) {
            e.polyline.width = saved.width;
            e.polyline.material = saved.material;
            e.polyline.clampToGround = saved.clampToGround;
            e.polyline.arcType = saved.arcType;
            e.polyline.zIndex = saved.zIndex;
          }
        } catch (_) {}
      }

      selectedEntities.length = 0;
      styleByEntityId.clear();
    };

    cancelFn = () => {
      cleanup();
      reject("cancelled");
    };

    const now = () => viewer?.clock?.currentTime ?? Cesium.JulianDate.now();

    const cloneStyle = (entity) => {
      const t = now();
      const polyline = entity?.polyline;
      const liveWidth = typeof polyline?.width?.getValue === "function" ? polyline.width.getValue(t) : (polyline?.width ?? 2.0);
      const width = Number.isFinite(entity?.__originalHighlightWidth) ? entity.__originalHighlightWidth : liveWidth;
      const material = polyline?.material ?? Cesium.Color.YELLOW;
      const clampToGround = typeof polyline?.clampToGround?.getValue === "function" ? !!polyline.clampToGround.getValue(t) : !!polyline?.clampToGround;
      const arcType = polyline?.arcType ?? Cesium.ArcType.GEODESIC;
      const zIndex = typeof polyline?.zIndex?.getValue === "function" ? polyline.zIndex.getValue(t) : (polyline?.zIndex ?? 0);
      return { width, material, clampToGround, arcType, zIndex };
    };

    const isEntityInDataSource = (ds, entity) => {
      if (!ds || !ds.entities) return false;
      if (typeof ds.entities.getById === "function") return !!ds.entities.getById(entity.id);
      if (typeof ds.entities.contains === "function") return !!ds.entities.contains(entity);
      return false;
    };

    const applyHighlight = (entity) => {
      if (!entity?.polyline) return;
      const saved = styleByEntityId.get(entity.id);
      if (!saved) return;

      entity.polyline.width = Math.max(1, (saved.width ?? 2) + 1);
      entity.polyline.material = Cesium.Color.RED;
      entity.polyline.clampToGround = saved.clampToGround;
      entity.polyline.arcType = saved.arcType;
      entity.polyline.zIndex = saved.zIndex;
    };

    const restoreStyle = (entity) => {
      if (!entity?.polyline) return;
      const saved = styleByEntityId.get(entity.id);
      if (!saved) return;

      entity.polyline.width = saved.width;
      entity.polyline.material = saved.material;
      entity.polyline.clampToGround = saved.clampToGround;
      entity.polyline.arcType = saved.arcType;
      entity.polyline.zIndex = saved.zIndex;
    };

    const toggleSelect = (entity) => {
      const idx = selectedEntities.findIndex((e) => e.id === entity.id);
      if (idx >= 0) {
        const [removed] = selectedEntities.splice(idx, 1);
        restoreStyle(removed);
        styleByEntityId.delete(removed.id);
        return;
      }

      if (!styleByEntityId.has(entity.id)) {
        styleByEntityId.set(entity.id, cloneStyle(entity));
      }

      selectedEntities.push(entity);
      applyHighlight(entity);
    };

    keyListener = (ev) => {
      if (ev.key === "Escape") {
        ev.preventDefault();
        cancelFn();
        return;
      }
      if (ev.key === "Enter") {
        ev.preventDefault();

        const payload = {
          __type: "PARALLEL_MULTI",
          distanceMeters,
          direction: side,
          selectedEntities: selectedEntities.slice(),
        };

        cleanup();
        resolve(payload);
      }
    };

    window.addEventListener("keydown", keyListener, true);

    handler.setInputAction((click) => {
      try {
        const picked = viewer.scene.pick(click.position);
        const src = picked && picked.id && picked.id.polyline ? picked.id : null;
        if (!src) return;

        if (defaultDataSource && !isEntityInDataSource(defaultDataSource, src)) return;

        toggleSelect(src);
      } catch (err) {
        console.error("[offsetSelectedPolylineOnce] Error:", err);
      }
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);
  });

  promise.cancel = () => cancelFn && cancelFn();

  return promise;
}
